/** Rule D-19: on a phone, a stop entry or a visit far from the stop pin needs a confirm. */

import type { LatLng, Place } from "./api";
import { distanceM } from "./offline/cache";
import { PHONE_OR_TOUCH } from "./offline/install";

export const FAR_M = 250;
export const FIX_TIMEOUT_MS = 5_000;
export const FIX_AGE_MS = 60_000;
const HINT_KEY = "xflow.placeHint";

export type Fix = { lat: number; lng: number; accuracy: number } | { error: "denied" | "no_fix" };
export type PlaceAsk = Exclude<Place["check"], "near">;
export type PlaceDecision = { ask: null; place: Place | null } | { ask: PlaceAsk; place: Place };

export const isPhone = (): boolean => typeof matchMedia === "function" && matchMedia(PHONE_OR_TOUCH).matches;

/** The pin of a stop: the tank pin, else the front door, as the nearby list uses. */
export const stopPin = (tank?: LatLng | null, door?: LatLng | null): LatLng | null => tank ?? door ?? null;

/** A desktop, a stop with no pin or an organization with the check off has no check (SO-1382). */
export const needsCheck = (phone: boolean, pin: LatLng | null | undefined, on: boolean | undefined = true): pin is LatLng =>
  on !== false && phone && pin != null;

export function decidePlace(phone: boolean, pin: LatLng | null | undefined, fix: Fix | null, farM: number = FAR_M): PlaceDecision {
  if (!needsCheck(phone, pin)) return { ask: null, place: null };
  if (!fix || "error" in fix) {
    const check = fix?.error ?? "no_fix";
    return { ask: check, place: { check } };
  }
  const distance = Math.round(distanceM(fix, pin));
  const accuracy = Math.round(fix.accuracy);
  const check = distance - accuracy > farM ? "far" : "near";
  const place: Place = { check, distance_m: distance, accuracy_m: accuracy };
  return check === "far" ? { ask: "far", place } : { ask: null, place };
}

/** The position of the phone. A recent cached position is good; no answer in time is no fix. */
export function readFix(timeoutMs: number = FIX_TIMEOUT_MS): Promise<Fix> {
  if (typeof navigator === "undefined" || !("geolocation" in navigator)) return Promise.resolve({ error: "no_fix" });
  return new Promise((done) => {
    const late = setTimeout(() => done({ error: "no_fix" }), timeoutMs + 1_000);
    navigator.geolocation.getCurrentPosition(
      (found) => {
        clearTimeout(late);
        done({ lat: found.coords.latitude, lng: found.coords.longitude, accuracy: found.coords.accuracy });
      },
      (error) => {
        clearTimeout(late);
        done({ error: error.code === error.PERMISSION_DENIED ? "denied" : "no_fix" });
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: FIX_AGE_MS },
    );
  });
}

export const metresText = (m: number): string => (m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${m} m`);

export function askText(place: Place, farM: number = FAR_M): string {
  if (place.check === "far")
    return `Your phone is ${metresText(place.distance_m ?? 0)} from this stop (GPS ±${metresText(place.accuracy_m ?? 0)}). Save only if you are at the stop. The office sees entries that are more than ${metresText(farM)} away.`;
  if (place.check === "denied") return "The app cannot see the location of this phone. Save only if you are at the stop. The office sees that the location was off.";
  return "The phone has no GPS position now. Save only if you are at the stop. The office sees that there was no position.";
}

export const ALLOW_TEXT = "To allow the location: in the browser, open the site settings (the icon at the left of the address), then set Location to Allow.";

/** The hint how to allow the location shows one time on each phone. */
export function takeHint(): boolean {
  try {
    if (localStorage.getItem(HINT_KEY)) return false;
    localStorage.setItem(HINT_KEY, "1");
  } catch {
    // No storage: the hint shows each time.
  }
  return true;
}

/** The short marker for the office, or null for a near entry or no check. */
export function placeMark(place: Place | null | undefined): string | null {
  if (!place || place.check === "near") return null;
  if (place.check === "far") return `${metresText(place.distance_m ?? 0)} from the stop (±${metresText(place.accuracy_m ?? 0)}), confirmed`;
  if (place.check === "denied") return "Location off, confirmed";
  return "No GPS position, confirmed";
}

/** A distance bucket for the log, so the log has no exact place. */
export function bucket(distance: number | null | undefined): string {
  if (distance == null) return "none";
  let low = 0;
  for (const high of [100, 250, 500, 1000, 5000]) {
    if (distance < high) return `${low}-${high}m`;
    low = high;
  }
  return ">5000m";
}
